import React, { useState } from "react";
import "./Login.css"
import {
    Heading, Card, CardBody, CardFooter, CardHeader, Container, VStack, Text, Button, Spacer, Image, 
    Divider, Input, FormControl, InputRightElement, InputGroup, useToast, FormErrorMessage, FormLabel,
} from "@chakra-ui/react";
import { AiFillWechat, AiOutlineMail } from 'react-icons/ai'
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ToffeeTitle from "../toffee-title.svg"
import { login, LoginRequest, me } from "../interface/user";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAuthStore } from "../stores/authStore";
import { LinkWithQuery } from "../components/LinkWithQuery";

function Login(props: any) {
    const navigate = useNavigate();
    const toast = useToast();
    const { setIdToken, setRefreshToken, setUser } = useAuthStore();
    
    const [ searchParams ] = useSearchParams();
    const phase = searchParams.get("phase");
    const redirect = searchParams.get("redirect");

    const [ isLoading, setIsLoading ] = useState(false);
    const [ passwordShown, setPasswordShown ] = useState(false);

    const schema = yup.object().shape({
        email: yup.string().email().required(),
        password: yup.string().min(8).required(),
    });

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(schema)
    });

    const onSubmit = (form: LoginRequest) => {
        setIsLoading(true);
        toast.promise(
            login(form).then(response => {
                setIdToken(response.data.token.idToken);
                setRefreshToken(response.data.token.refreshToken);
                me().then(response => {
                    setUser(response.data.user);
                }).catch(error => {
                    throw error.response?.data?.message || error.message || "Unknown error occurred";
                });
                navigate(redirect || "/");
            }).catch(error => {
                throw error.response?.data?.message || error.message || "Unknown error occurred";
            }).finally(() => {
                setIsLoading(false);
            }),
            {
                loading: {
                    title: "Logging in...",
                    description: "Please wait while we log you in",
                },
                success: {
                    title: "Logged in successfully",
                    description: "You can now start using Toffee",
                },
                error: {
                    title: "Failed to login",
                    description: "Please try again later",
                }
            }
        );
    }

    return (
        <Container id={'login-page'} maxW={500}>
            <VStack spacing={6}>
                <Card variant={"elevated"} minW={400}>
                    <CardHeader>
                        <VStack spacing={3}>
                            <Link to={"/"}>
                                <Image
                                    htmlWidth={150}
                                    src={ToffeeTitle}
                                />
                            </Link>
                            <Heading size={"md"}>Welcome</Heading>
                            <Divider/>
                        </VStack>
                    </CardHeader>
                    <CardBody>
                        {phase === null && (
                            <VStack spacing={8}>
                                <Button variant={"outline"} leftIcon={<AiFillWechat/>}>
                                    Login with Wechat
                                </Button>
                                <Button variant={"outline"} leftIcon={<AiOutlineMail/>}>
                                    <LinkWithQuery to={"/login?phase=email"}>
                                        Login with Email
                                    </LinkWithQuery>
                                </Button>
                            </VStack>
                        )}
                        {phase === "email" && (
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <VStack spacing={8}>
                                    <FormControl id={"Email"} isInvalid={!!errors.email}>
                                        <FormLabel>Email</FormLabel>
                                    <Input 
                                        {...register("email")}
                                        placeholder={"Email Address"}
                                    />
                                    {errors.email && <FormErrorMessage>{errors.email.message}</FormErrorMessage>}
                                    </FormControl>
                                    <FormControl id={"Password"} isInvalid={!!errors.password}>
                                        <FormLabel>Password</FormLabel>
                                        <InputGroup>
                                            <Input 
                                                {...register("password")}
                                                type={passwordShown ? "text" : "password"} 
                                                placeholder={"Password"}
                                            />
                                            <InputRightElement width={"4.5rem"}>
                                                <Button size={"sm"} onClick={() => setPasswordShown(!passwordShown)}>
                                                    {passwordShown ? "Hide" : "Show"}
                                                </Button>
                                            </InputRightElement>
                                        </InputGroup>
                                        {errors.password && <FormErrorMessage>{errors.password.message}</FormErrorMessage>}
                                    </FormControl>
                                    <Button 
                                        colorScheme={"teal"} 
                                        width={"100%"} 
                                        isLoading={isLoading}
                                        type={"submit"}
                                    >
                                        Login
                                    </Button>
                                </VStack>
                            </form>
                        )}
                    </CardBody>
                    <CardFooter>
                        <Spacer/>
                        <Text paddingX={2}>Don't have an account?</Text>
                        <Link to={"/signup"}>
                            Signup
                        </Link>
                    </CardFooter>
                </Card>
                <Container>
                    <Text fontSize={"sm"}>
                        When you link your Facebook, Google, or Yahoo account, Kaggle collects certain information stored in that account that you have configured to make available. By linking your accounts, you authorize Kaggle to access and use your account on the third party service in connection with your use of kaggle.com.
                    </Text>
                </Container>
            </VStack>
        </Container>
    );
}

export default Login;